/**
 * @typedef {{response?: {status?: number, headers?: unknown, data?: unknown}, config?: {headers?: unknown}}} AuthError
 */

/**
 * Reads a header from AxiosHeaders or a plain object.
 * @param {unknown} headers AxiosHeaders or plain-object headers
 * @param {string} name header name
 * @returns {string|undefined}
 */
export const getHeaderValue = (headers, name) => {
  if (headers && typeof headers === 'object') {
    const headerCollection =
      /** @type {{get?: (header: string) => unknown}} */ (headers);
    if (typeof headerCollection.get === 'function') {
      const value = headerCollection.get(name);
      if (typeof value === 'string') return value;
    }

    const plainHeaders = /** @type {Record<string, unknown>} */ (headers);
    const lowerName = name.toLowerCase();
    const key = Object.keys(plainHeaders).find(
      (headerName) => headerName.toLowerCase() === lowerName
    );
    const value = key ? plainHeaders[key] : undefined;
    return typeof value === 'string' ? value : undefined;
  }

  return undefined;
};

/**
 * Returns the bearer challenge when it explicitly reports an invalid token.
 * @param {AuthError} error Axios-like error
 * @returns {boolean}
 */
export const hasInvalidTokenChallenge = (error) => {
  const challenge = getHeaderValue(
    error?.response?.headers,
    'WWW-Authenticate'
  );
  return Boolean(
    challenge &&
      /^Bearer\s+error="invalid_token"(?:\s|,|$)/i.test(challenge.trim())
  );
};

/**
 * Classifies definitive auth failures without inspecting human-readable text.
 * The bearer challenge is authoritative; the JSON code is only a fallback
 * when no challenge is present.
 * @param {AuthError} error Axios-like error
 * @returns {boolean}
 */
export const isInvalidOrRevokedCredentialError = (error) => {
  if (error?.response?.status !== 401) return false;

  const challenge = getHeaderValue(
    error?.response?.headers,
    'WWW-Authenticate'
  );
  if (challenge !== undefined) return hasInvalidTokenChallenge(error);

  const data = error?.response?.data;
  return (
    data !== null &&
    typeof data === 'object' &&
    /** @type {{code?: unknown}} */ (data).code === 'AUTH_TOKEN_EXPIRED'
  );
};

/**
 * Classifies failures that should preserve the current auth session.
 * @param {AuthError} error Axios-like error
 * @returns {boolean}
 */
export const isTransientAuthFailure = (error) =>
  !error?.response || (error.response.status ?? 0) >= 500;
